import { ref } from 'vue'
import { MFA_ERROR_MESSAGES, getMfaErrorMessage } from '../constants/errorMessages'

export interface MfaLoginResponse {
  success: boolean
  message: string
  expires_in?: number
  mfa_verified?: boolean
  verification_token?: string
}

export const useMfaLogin = () => {
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Send verification code for login
  const sendLoginCode = async (firebaseUid: string): Promise<MfaLoginResponse> => {
    try {
      isLoading.value = true
      error.value = null

      const response = await $postApi<MfaLoginResponse>('/api/mfa-login/send-code', {
        body: { firebase_uid: firebaseUid }
      })

      if (response.success) {
        return response
      }

      throw new Error(response.message || MFA_ERROR_MESSAGES.SEND_CODE_FAILED)
    } catch (err: any) {
      const errorMessage = getMfaErrorMessage(err) || MFA_ERROR_MESSAGES.SEND_CODE_FAILED
      error.value = errorMessage
      
      // Create a new error with the proper message and preserve the response data
      const apiError = new Error(errorMessage) as any
      apiError.data = err.data
      apiError.status = err.status
      apiError.response = err.response
      throw apiError
    } finally {
      isLoading.value = false
    }
  }

  // Verify code for login
  const verifyLoginCode = async (firebaseUid: string, code: string): Promise<MfaLoginResponse> => {
    try {
      isLoading.value = true
      error.value = null

      const response = await $postApi<MfaLoginResponse>('/api/mfa-login/verify', {
        body: { firebase_uid: firebaseUid, code: code }
      })

      if (response.success) {
        return response
      }

      throw new Error(response.message || MFA_ERROR_MESSAGES.VERIFY_CODE_FAILED)
    } catch (err: any) {
      const errorMessage = getMfaErrorMessage(err)
      error.value = errorMessage
      
      // Create a new error with the proper message and preserve the response data
      const apiError = new Error(errorMessage) as any
      apiError.data = err.response?._data || err.response?.data || err.data
      apiError.status = err.status || err.response?.status
      apiError.response = err.response
      throw apiError
    } finally {
      isLoading.value = false
    }
  }

  return {
    isLoading,
    error,
    sendLoginCode,
    verifyLoginCode
  }
}
